import cloneDeep from 'lodash/cloneDeep';
import type { Item, LoadedItem, SearchResultHit } from '@mimir/common/dto/searchResultHit';
import type Relations from '@mimir/common/dto/relationModels';
import type { RelatedItem } from '@mimir/common/dto/relationModels';
import type { QuickPreviewTabName } from '@mimir/common/types/UIOnlySections';
import type { MetadataSnapshot } from '@mimir/common/dto/mdfModels';
import type { ItemMediaPlayerOps, PostLoadSeek } from './itemMediaPlayerOps';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import type { ThumbnailClick, ThumbnailStripClick } from '../pages/search/components/search-page-interfaces';
import { getGlobalStore } from '@mimir/web/store';
import Vue, { reactive } from 'vue';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { VueDeleteValue, VueSetValue } from '@mimir/common/util/general';
import { handleMdfUpdatesToItem } from '@mimir/common/mdf/update';
import { ItemState } from '@mimir/common/dto/itemState';
import { isDeleting } from '@mimir/common/dto/itemModels';
import { AsyncOperationState } from '@mimir/common/dto/asyncOperationPrimitives';
import { logErrorAndShowToast } from '../util/uiError';
import { OperationApiContext } from '@mimir/common/util/operationApiContext';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { isNumber } from 'lodash';
import { toRelatedItem } from './toRelatedItem';
import { getMediaDuration } from '@mimir/common/util/item';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import { ApiActions } from '@mimir/client-common/util/apiActions';
import { storeDispatchLoadCurrentItemMetadata } from '../store/storeDispatch';

export interface LoadItemInQuickPreviewPayload {
  item: Item | null,
  fromHistory?: true,
  autoPlayFrom?: number,
  quickPreviewMediaPlayerOps: ItemMediaPlayerOps,
  timeToSeekInMs?: number,
}

export interface LoadRelatedItemInQuickPreviewPayload {
  event: ThumbnailStripClick | ThumbnailClick,
  quickPreviewMediaPlayerOps: ItemMediaPlayerOps
}

export interface SetCollapsedPayload {
  shouldBeCollapsed: boolean,
  quickPreviewMediaPlayerOps: ItemMediaPlayerOps
}

export interface SetIsItemMetadataLoadingPayload {
  id: string,
  loading: boolean
}

export interface SetRelatedItemhasBeenUploadedToInRelationsPayload {
  id: string,
  hasBeenUploadedTo: boolean,
}

export interface SetSearchItemPayload {
  item: Item | null,
}

export interface UpdateCurrentPreviewItemPayload {
  id: string;
  update: Partial<LoadedItem>;
  retrievedFields?: (keyof LoadedItem)[]; // fields that were fetched from the server
}


interface MutableQuickPreviewState {
  currentItem: Item | null,
  selectedRelatedItem: RelatedItem | null,
  selectedTab: QuickPreviewTabName,
  previewCollapsed: boolean,
  recentlyPreviewed: Item[],
  isItemMetadataLoadingMap: Record<string, boolean>,
  mediaPreviewCollapsed: boolean,
}

export type QuickPreviewState = Readonly<MutableQuickPreviewState>;

export interface QuickPreviewOps {
  // Mutations
  setRelations(payload: Relations): void;
  setRelatedItemHasBeenUploadedToInRelations(payload: SetRelatedItemhasBeenUploadedToInRelationsPayload): void;
  setIsItemMetadataLoading(payload: SetIsItemMetadataLoadingPayload): void;
  setSelectedQuickPreviewTab(payload: QuickPreviewTabName): void;
  setSearchItem(payload: SetSearchItemPayload): void;
  updateCurrentPreviewItem(payload: UpdateCurrentPreviewItemPayload): void;
  setSearchPlayerItemMetadataSnapshot(data: MetadataSnapshot | undefined): void;
  setMediaPreviewCollapsed(payload: boolean): void;

  // Actions
  updateQuickPreviewReference(hits: SearchResultHit[]): void;
  emitPlayerError(ev: ErrorEvent): void;
  setCollapsed(payload: SetCollapsedPayload): void;
  loadItemInQuickPreview(payload: LoadItemInQuickPreviewPayload): void;
  loadRelatedItemInQuickPreview(payload: LoadRelatedItemInQuickPreviewPayload): void;
  unloadSearchPlayer(quickPreviewMediaPlayerOps: ItemMediaPlayerOps): void;
  updateCurrentPreviewItemMutation(payload: UpdateCurrentPreviewItemPayload): void;
}

export interface QuickPreview {
  state: QuickPreviewState,
  ops: QuickPreviewOps,
}

interface InternalState {
  relations: Relations,
}

export function createQuickPreview(): QuickPreview {

  const internalState = reactive<InternalState>({
    relations: [],
  });

  const mutableState : MutableQuickPreviewState = reactive({
    currentItem: null,
    selectedRelatedItem: null,
    selectedTab: 'metadata',
    previewCollapsed: false,
    recentlyPreviewed: [],
    isItemMetadataLoadingMap: {},
    mediaPreviewCollapsed: false,
  });

  const quickPreview = {
    state: mutableState,
    ops: {
      setRelations(payload: Relations): void {
        internalState.relations = payload;
      },
      setRelatedItemHasBeenUploadedToInRelations(payload: SetRelatedItemhasBeenUploadedToInRelationsPayload): void {
        const relatedItem = internalState.relations.find(r => r.item.id === payload.id);
        if (relatedItem) {
          Vue.set(relatedItem.item, 'hasBeenUploadedTo', payload.hasBeenUploadedTo);
        }
      },
      setIsItemMetadataLoading(payload: SetIsItemMetadataLoadingPayload): void {
        Vue.set(mutableState.isItemMetadataLoadingMap, payload.id, payload.loading);
      },
      setSelectedQuickPreviewTab(payload: QuickPreviewTabName): void {
        mutableState.selectedTab = payload;
      },
      setSearchItem(payload: SetSearchItemPayload): void {
        const { item } = payload;
        if (item && item.id === mutableState.currentItem?.id) {
          /**
           * TODO: If we already have this item as the currentItem, we should
           * probably updating the existing item instead of overwriting it, since
           * we mutate the currentItem with client state from various places.
           */
          console.warn('setSearchItem: item is already the current item');
        }

        mutableState.currentItem = cloneDeep(item);
        if (mutableState.currentItem !== null) {
          addToRecentlyPreviewed(mutableState.recentlyPreviewed, mutableState.currentItem);
        }
      },
      updateCurrentPreviewItem(payload: UpdateCurrentPreviewItemPayload): void {
        if (!mutableState.currentItem || payload.id !== mutableState.currentItem.id) return;
        const keysToIterateOver = Object.keys(payload.update) as (keyof LoadedItem)[];
        const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
        const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
        for (const prop of keysToIterateOver) {
          if (prop === 'metadata' && payload.update.metadata !== undefined) {
            let metadataVersion = payload.update.metadataVersion;
            if (metadataVersion == undefined) {
              console.warn('updateCurrentPreviewItem: metadata update without metadataVersion');
              metadataVersion = 0;
            }
            handleMdfUpdatesToItem(mutableState.currentItem, payload.update.metadata, metadataVersion, vueSet, vueDeleter);
          } else if (payload.update[prop] === undefined) {
            Vue.delete(mutableState.currentItem, prop);
          } else {
            Vue.set(mutableState.currentItem, prop, payload.update[prop]);
          }
        }
      },
      setSearchPlayerItemMetadataSnapshot(data: MetadataSnapshot | undefined): void {
        if (mutableState.currentItem) {
          Vue.set(mutableState.currentItem, 'metadataSnapshot', data);
        }
      },
      setMediaPreviewCollapsed(payload: boolean): void {
        mutableState.mediaPreviewCollapsed = payload;
      },

      // Actions
      updateQuickPreviewReference(hits: SearchResultHit[]): void {
        const store = getGlobalStore();

        const currentItem = mutableState.currentItem;
        if (currentItem === null) return;
        const match = hits.find(hit => hit.id === currentItem.id);
        if (match) {
          // We need to make sure the currentItem has updated metaData.
          if (!mutableState.isItemMetadataLoadingMap[currentItem.id]) {
            storeDispatchLoadCurrentItemMetadata({ quickPreview }).catch((err) => `Error fetching currentItem metadata ${err}`);
          }
          // If we found a match, we should also mark it as selected.
          store.commit('addToSelectedSearchItems', match);
          addToRecentlyPreviewed(mutableState.recentlyPreviewed, currentItem);
        }
      },
      emitPlayerError(ev: ErrorEvent): void {
        // Suppress error for now if its a placeholder. This should be removed once we have a quick preview
        // component for place holders (likely a drag and drop component. Issue #426)
        const item = mutableState.currentItem;
        if (item !== null &&
            item.itemType === ItemType.VIDEO &&
            item.itemState !== ItemState.NEW &&
            item.itemState !== ItemState.HIGHRES_AVAILABLE &&
            !isDeleting(item) &&
            item.transcodingState &&
            item.transcodingState !== AsyncOperationState.PROGRESSING &&
            item.transcodingState !== AsyncOperationState.INITIATED) {
          logErrorAndShowToast('Failed to load video', ev);
        }
      },
      setCollapsed(payload: SetCollapsedPayload): void {
        const { shouldBeCollapsed, quickPreviewMediaPlayerOps } = payload;
        if (shouldBeCollapsed) {
          quickPreviewMediaPlayerOps.pause();
        }
        mutableState.previewCollapsed = shouldBeCollapsed;
      },
      loadItemInQuickPreview(payload: LoadItemInQuickPreviewPayload): void {
        const store = getGlobalStore();
        const { item, fromHistory, autoPlayFrom, quickPreviewMediaPlayerOps, timeToSeekInMs } = payload;
        if (item !== null && quickPreviewMediaPlayerOps.getInitializedObs().currentValue() && (item.itemType === ItemType.VIDEO || item.itemType === ItemType.AUDIO)) {
          const apiContext = new OperationApiContext(globalBackendClient, new ExponentialBackoff());
          const apiActions = new ApiActions(apiContext);
          const autoplay = isNumber(autoPlayFrom) ? true : !quickPreviewMediaPlayerOps.getPausedObs().currentValue();
          const useNewBufferingLogic = (store.getters.featureFlags as TenantConfigDto['featureFlags'])?.useNewBufferingLogic ?? false;
          const flags = {
            useNewBufferingLogic,
          };
          const seekPositionInMs = (isNumber(autoPlayFrom) ? autoPlayFrom : (timeToSeekInMs ?? 0));
          const postLoadOperation: PostLoadSeek = {
            type: 'seek',
            positionInMs: seekPositionInMs,
            play: autoplay,
          };
          quickPreviewMediaPlayerOps.setItemId(apiActions, item.id, item.itemType, flags, { postLoadOperation });
        }
        mutableState.selectedRelatedItem = null;
        if (fromHistory) {
          // When navigating via history, to reduce user confusion we clear selection in search, because there's no guarantee that the item
          // that will be previewed will be visible in the current search result.
          store.commit('clearSelectedSearchItems');
        }
        this.setSearchItem({ item });
        return;
      },
      loadRelatedItemInQuickPreview(payload: LoadRelatedItemInQuickPreviewPayload): void {
        const store = getGlobalStore();
        const { event, quickPreviewMediaPlayerOps } = payload;
        const wasPlaying = !quickPreviewMediaPlayerOps.getPausedObs().currentValue();
        if (event.item !== mutableState.selectedRelatedItem) {
          const relatedItem = toRelatedItem(event.item);
          if (relatedItem === null) {
            throw new Error('Requested item to load is not a related item');
          }
          mutableState.selectedRelatedItem = relatedItem;
          addToRecentlyPreviewed(mutableState.recentlyPreviewed, mutableState.selectedRelatedItem);
          if (event.item.itemType === ItemType.VIDEO || event.item.itemType === ItemType.AUDIO) {
            const apiContext = new OperationApiContext(globalBackendClient, new ExponentialBackoff());
            const apiActions = new ApiActions(apiContext);
            const useNewBufferingLogic = (store.getters.featureFlags as TenantConfigDto['featureFlags'])?.useNewBufferingLogic ?? false;
            const flags = {
              useNewBufferingLogic,
            };

            quickPreviewMediaPlayerOps.setItemId(apiActions, event.item.id, event.item.itemType, flags);
            mutableState.previewCollapsed = false;
          } else {
            quickPreviewMediaPlayerOps.clear();
          }
        }

        const mediaDuration = getMediaDuration(event.item);
        if (mediaDuration)  {
          let time = 0;
          if (isThumbnailStripClick(event) && mediaDuration) {
            time = mediaDuration * event.percentageOfVideo;
          }
          setTimeout(() => {
            if (wasPlaying) {
              quickPreviewMediaPlayerOps.play(time);
            } else {
              quickPreviewMediaPlayerOps.seekToVirtualClipMediaPosition({ approximate: time / 1000.0 });
            }
          }, 100);
        }
      },
      unloadSearchPlayer(quickPreviewMediaPlayerOps: ItemMediaPlayerOps): void {
        quickPreviewMediaPlayerOps.clear();
      },
      updateCurrentPreviewItemMutation(payload: UpdateCurrentPreviewItemPayload) {
        const currentItem = mutableState.currentItem;
        if (!currentItem || payload.id !== currentItem.id) return;

        /*
          `payload.retrievedFields` contains a list of keys fetched from the server.
          Since `undefined` values are not transmitted over the API, we combine the
          fetched keys with those available in the `payload.update` object.

          This ensures that the client state is updated with the most recent data
          while correctly handling stale state updates.
        */
        const uniqueKeys = new Set([...Object.keys(payload.update), ...(payload.retrievedFields ?? [])]);
        const keysToIterateOver = Array.from(uniqueKeys) as (keyof LoadedItem)[];

        const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
        const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
        for (const prop of keysToIterateOver) {
          if (prop === 'metadata' && payload.update.metadata !== undefined) {
            let metadataVersion = payload.update.metadataVersion;
            if (metadataVersion == undefined) {
              console.warn('updateCurrentPreviewItemMutation: metadata update without metadataVersion');
              metadataVersion = 0;
            }
            handleMdfUpdatesToItem(currentItem, payload.update.metadata, metadataVersion, vueSet, vueDeleter);
          } else if (payload.update[prop] === undefined) {
            Vue.delete(currentItem, prop);
          } else {
            Vue.set(currentItem, prop, payload.update[prop]);
          }
        }
      }
    }
  };

  return quickPreview;
}

/**
 * If previewable, track recently previewed items. If an item is already in the list, to avoid duplicates, we remove the old copy
 * and add a new clone to the top of the list. Items may have changed in the meantime.
 *
 */
function addToRecentlyPreviewed(recentlyPreviewedList: (Item | RelatedItem)[], itemToAdd: Item | RelatedItem) {


  if (!isPreviewable(itemToAdd)) {
    return;
  }

  const indexOfExisting = recentlyPreviewedList.findIndex(item => item.id === itemToAdd.id);
  if (indexOfExisting >= 0) {
    recentlyPreviewedList.splice(indexOfExisting, 1);
  }
  recentlyPreviewedList.unshift(cloneDeep(itemToAdd));
};

function isPreviewable(item: Item | RelatedItem): boolean {
  return item.itemType !== ItemType.FOLDER;
};

function isThumbnailStripClick(event: ThumbnailStripClick | ThumbnailClick): event is ThumbnailStripClick {
  return 'percentageOfVideo' in event;
};
